import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Home, Calculator, Shield, Clock } from 'lucide-react';

const HomeLoan = () => {
  const features = [
    {
      icon: <Home className="h-6 w-6" />,
      title: "High Loan Amount",
      description: "Get attractive loan amounts for your dream home"
    },
    {
      icon: <Calculator className="h-6 w-6" />,
      title: "Competitive Rates",
      description: "Interest rates starting from 10% p.a. with flexible tenure"
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: "Minimal Processing Fee",
      description: "Low processing fees with transparent charges"
    },
    {
      icon: <Clock className="h-6 w-6" />,
      title: "Quick Approval",
      description: "Fast processing with approval in just 7-10 days"
    }
  ];

  const eligibility = [
    "Age: 23 to 62 years (at loan maturity)",
    "Employment: Salaried/Self-employed/Professional",
    "Monthly Income: Minimum ₹25,000 (Salaried), ₹40,000 (Self-employed)",
    "Work Experience: Minimum 3 years total, 1 year current",
    "CIBIL Score: 700 and above",
    "Property: Ready possession or under construction"
  ];

  const documents = [
    "Identity & Address Proof (Aadhaar/Passport/Voter ID)",
    "Income Documents (Salary slips/ITR/Bank statements)",
    "Property Documents (Sale deed/Agreement/Approvals)",
    "Bank Statements (Last 6 months)",
    "Employment Proof (Appointment letter/Business proof)",
    "Property Valuation Report"
  ];

  const homeTypes = [
    "Ready to Move Apartments",
    "Under Construction Properties",
    "Independent Houses/Villas",
    "Residential Plots",
    "Home Extension/Renovation",
    "Balance Transfer from other banks"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-indigo-100 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight">
                  Make Your Dream
                  <span className="block" style={{ color: '#340C7F' }}>
                    Home a Reality
                  </span>
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Get attractive home loans with competitive interest rates 
                  starting from 10% p.a. and flexible repayment options up to 30 years.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold" style={{ color: '#340C7F' }}>Attractive</p>
                  <p className="text-sm text-gray-600">Loan Amount</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold" style={{ color: '#340C7F' }}>10%*</p>
                  <p className="text-sm text-gray-600">Interest Rate</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold" style={{ color: '#340C7F' }}>Up to 30</p>
                  <p className="text-sm text-gray-600">Years Tenure</p>
                </div>
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold" style={{ color: '#340C7F' }}>7-10 Days</p>
                  <p className="text-sm text-gray-600">Approval Time</p>
                </div>
              </div>

              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white rounded-lg hover:opacity-90 transition-opacity shadow-lg"
                style={{ backgroundColor: '#340C7F' }}
              >
                Apply Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" 
                alt="Home Loan" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Why Choose Our <span style={{ color: '#340C7F' }}>Home Loan?</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center space-y-4 p-6 rounded-xl hover:shadow-lg transition-shadow">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full text-white" 
                     style={{ backgroundColor: '#340C7F' }}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Home Types Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Finance Any Type of <span style={{ color: '#340C7F' }}>Property</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Whether you're buying your first home or upgrading to a bigger one, we have the right loan for you.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {homeTypes.map((type, index) => (
              <div key={index} className="bg-white rounded-lg p-6 text-center shadow-sm hover:shadow-md transition-shadow">
                <CheckCircle className="h-8 w-8 mx-auto mb-3" style={{ color: '#340C7F' }} />
                <p className="font-medium text-gray-900">{type}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility & Documents Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Eligibility Criteria */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-gray-900">
                  Eligibility <span style={{ color: '#340C7F' }}>Criteria</span>
                </h2>
                <p className="text-lg text-gray-600">
                  Check if you meet our eligibility requirements for home loan approval.
                </p>
              </div>

              <ul className="space-y-4">
                {eligibility.map((criterion, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{criterion}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Required Documents */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-gray-900">
                  Required <span style={{ color: '#340C7F' }}>Documents</span>
                </h2>
                <p className="text-lg text-gray-600">
                  Keep these documents ready for a smooth home loan application process.
                </p>
              </div>

              <ul className="space-y-4">
                {documents.map((document, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{document}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Simple <span style={{ color: '#340C7F' }}>Application Process</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get your home loan approved in just a few simple steps.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Apply Online', description: 'Fill out our simple online application form' },
              { step: '02', title: 'Document Verification', description: 'Submit required documents for verification' },
              { step: '03', title: 'Property Evaluation', description: 'Our team will evaluate the property value' },
              { step: '04', title: 'Loan Approval', description: 'Get approval and loan disbursal' }
            ].map((process, index) => (
              <div key={index} className="text-center space-y-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full text-white text-xl font-bold"
                     style={{ backgroundColor: '#340C7F' }}>
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Home Loan Calculator Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Home Loan <span style={{ color: '#340C7F' }}>Benefits Calculator</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Understand the financial benefits and tax savings with our home loan
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Tax Benefits",
                amount: "Up to ₹3.5 Lakhs",
                description: "Principal repayment u/s 80C + Interest deduction u/s 24(b)",
                details: ["₹1.5L - Principal (80C)", "₹2L - Interest (24b)", "Additional ₹50K for first-time buyers"]
              },
              {
                title: "EMI Savings",
                amount: "₹15,000 - ₹25,000",
                description: "Monthly rent savings by owning your home",
                details: ["No monthly rent", "Building equity", "Price appreciation benefits"]
              },
              {
                title: "Loan Amount",
                amount: "Up to ₹10 Crores",
                description: "High loan amount based on property value and income",
                details: ["85% of property value", "Income-based calculation", "Joint applicant benefits"]
              }
            ].map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-center space-y-4">
                  <h3 className="text-xl font-bold text-gray-900">{benefit.title}</h3>
                  <div className="text-3xl font-bold" style={{ color: '#340C7F' }}>
                    {benefit.amount}
                  </div>
                  <p className="text-gray-600">{benefit.description}</p>
                  <ul className="space-y-2 text-left">
                    {benefit.details.map((detail, idx) => (
                      <li key={idx} className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-sm text-gray-700">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location-Based Offers */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              City-Specific <span style={{ color: '#340C7F' }}>Home Loan Offers</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Special interest rates and offers based on your city's real estate market
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                city: "Mumbai",
                rate: "16.0% - 18.5%",
                specialOffer: "No processing fee for loans above ₹50 lakhs",
                avgProperty: "₹8,500/sq ft",
                features: ["Premium location rates", "Builder tie-ups", "Fast approvals"]
              },
              {
                city: "Pune",
                rate: "16.5% - 19.0%",
                specialOffer: "Free property evaluation and legal verification",
                avgProperty: "₹6,200/sq ft",
                features: ["IT hub proximity", "Infrastructure projects", "Growth potential"]
              },
              {
                city: "Ahmedabad",
                rate: "17.0% - 19.5%",
                specialOffer: "Extended tenure up to 35 years",
                avgProperty: "₹4,800/sq ft",
                features: ["Affordable housing", "Industrial growth", "Government support"]
              }
            ].map((city, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div className="space-y-4">
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-gray-900">{city.city}</h3>
                    <div className="text-2xl font-bold mt-2" style={{ color: '#340C7F' }}>
                      {city.rate}
                    </div>
                    <p className="text-sm text-gray-600">Interest Rate Range</p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-900 mb-2">Special Offer:</p>
                    <p className="text-sm text-gray-600">{city.specialOffer}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-gray-900 mb-2">Avg. Property Rate:</p>
                    <p className="text-lg font-bold" style={{ color: '#340C7F' }}>{city.avgProperty}</p>
                  </div>
                  
                  <ul className="space-y-1">
                    {city.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#340C7F' }}>
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Ready to Buy Your Dream Home?
          </h2>
          <p className="text-xl text-blue-100">
            Take the first step towards homeownership with our attractive home loan offers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-colors shadow-lg transform hover:scale-105"
            >
              Apply for Home Loan
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white border-2 border-white rounded-lg hover:bg-white hover:text-gray-900 transition-colors transform hover:scale-105"
            >
              Calculate EMI
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomeLoan;